<template>
  <div class="relative aspect-[16/10] min-h-[12.5rem] overflow-hidden bg-zinc-100 dark:bg-zinc-800/80">
    <div ref="mapContainer" class="absolute inset-0" />

    <div
      v-if="etaLabel"
      class="panel-glass-card pointer-events-none absolute end-4 top-4 z-[500] rounded-2xl px-3 py-2"
    >
      <p class="panel-glass-content text-[10px] font-medium text-zinc-700 dark:text-zinc-200">
        {{ etaLabel }}
      </p>
    </div>

    <div
      v-if="mapError"
      class="absolute inset-0 z-[500] flex items-center justify-center bg-zinc-100/90 px-6 text-center text-sm text-zinc-500 dark:bg-zinc-900/90 dark:text-zinc-400"
    >
      {{ mapError }}
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const lightTiles = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
const darkTiles = 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png'

export default {
  name: 'ShipmentTrackingMap',

  props: {
    origin: {
      type: Object,
      required: true,
    },
    destination: {
      type: Object,
      required: true,
    },
    progress: {
      type: Number,
      default: 0,
    },
    etaLabel: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    mapError: null,
    isDark: false,
  }),

  watch: {
    origin: {
      deep: true,
      handler() {
        this.renderRoute()
      },
    },
    destination: {
      deep: true,
      handler() {
        this.renderRoute()
      },
    },
    progress() {
      this.renderRoute()
    },
    isDark() {
      this.applyTileLayer()
    },
  },

  mounted() {
    this.syncTheme()
    this.themeObserver = new MutationObserver(() => {
      this.syncTheme()
    })
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    })

    this.$nextTick(() => {
      this.initializeMap()
      this.observeMapContainer()
    })
  },

  beforeUnmount() {
    this.themeObserver?.disconnect()
    this.resizeObserver?.disconnect()
    this.clearRouteOverlays()
    this.mapInstance?.remove()
    this.mapInstance = null
    this.tileLayer = null
    this.routeLayer = null
  },

  methods: {
    syncTheme() {
      this.isDark = document.documentElement.classList.contains('dark')
    },

    clampProgress(value) {
      return Math.min(100, Math.max(0, value))
    },

    toRadians(value) {
      return value * (Math.PI / 180)
    },

    distanceBetween(start, end) {
      const earthRadius = 6371000
      const deltaLat = this.toRadians(end.lat - start.lat)
      const deltaLng = this.toRadians(end.lng - start.lng)
      const startLat = this.toRadians(start.lat)
      const endLat = this.toRadians(end.lat)
      const haversine = Math.sin(deltaLat / 2) ** 2
        + Math.cos(startLat) * Math.cos(endLat) * Math.sin(deltaLng / 2) ** 2

      return 2 * earthRadius * Math.asin(Math.sqrt(haversine))
    },

    interpolateCoordinates(start, end, ratio) {
      return {
        lat: start.lat + ((end.lat - start.lat) * ratio),
        lng: start.lng + ((end.lng - start.lng) * ratio),
        label: start.label,
      }
    },

    buildGeodesicRoute(origin, destination) {
      const points = []
      const steps = 64

      for (let index = 0; index <= steps; index += 1) {
        const ratio = index / steps
        const startLat = this.toRadians(origin.lat)
        const startLng = this.toRadians(origin.lng)
        const endLat = this.toRadians(destination.lat)
        const endLng = this.toRadians(destination.lng)
        const startX = Math.cos(startLat) * Math.cos(startLng)
        const startY = Math.cos(startLat) * Math.sin(startLng)
        const endX = Math.cos(endLat) * Math.cos(endLng)
        const endY = Math.cos(endLat) * Math.sin(endLng)
        const dot = Math.max(-1, Math.min(1, (startX * endX) + (startY * endY) + (Math.sin(startLat) * Math.sin(endLat))))
        const distance = Math.acos(dot)
        const sinDistance = Math.sin(distance)

        if (sinDistance === 0) {
          points.push(origin)
          continue
        }

        const startWeight = Math.sin((1 - ratio) * distance) / sinDistance
        const endWeight = Math.sin(ratio * distance) / sinDistance
        const x = (startWeight * startX) + (endWeight * endX)
        const y = (startWeight * startY) + (endWeight * endY)
        const z = (startWeight * Math.sin(startLat)) + (endWeight * Math.sin(endLat))
        const lat = Math.atan2(z, Math.sqrt((x * x) + (y * y)))
        const lng = Math.atan2(y, x)

        points.push({
          lat: lat * (180 / Math.PI),
          lng: lng * (180 / Math.PI),
          label: origin.label,
        })
      }

      return points
    },

    getPointAlongPath(path, progress) {
      if (path.length === 0) {
        return null
      }

      if (path.length === 1) {
        return path[0]
      }

      const normalizedProgress = this.clampProgress(progress) / 100
      const segments = []
      let totalLength = 0

      for (let index = 0; index < path.length - 1; index += 1) {
        const start = path[index]
        const end = path[index + 1]
        const length = this.distanceBetween(start, end)
        segments.push({ start, end, length })
        totalLength += length
      }

      if (totalLength === 0) {
        return path[0]
      }

      const targetDistance = totalLength * normalizedProgress
      let walked = 0

      for (const segment of segments) {
        if (walked + segment.length >= targetDistance) {
          const ratio = (targetDistance - walked) / segment.length
          return this.interpolateCoordinates(segment.start, segment.end, ratio)
        }

        walked += segment.length
      }

      return path[path.length - 1]
    },

    buildMarkerIcon(label, color) {
      return L.divIcon({
        className: '',
        html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:9999px;background:${color};color:#fff;font-size:11px;font-weight:700;border:2px solid #fff;">${label}</div>`,
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      })
    },

    async fetchDrivingRoute(origin, destination) {
      const endpoint = `https://router.project-osrm.org/route/v1/driving/${origin.lng},${origin.lat};${destination.lng},${destination.lat}?overview=full&geometries=geojson`

      try {
        const response = await fetch(endpoint)

        if (!response.ok) {
          return null
        }

        const data = await response.json()

        if (data.code !== 'Ok' || !data.routes?.[0]?.geometry?.coordinates) {
          return null
        }

        return data.routes[0].geometry.coordinates.map(([lng, lat]) => ({
          lat,
          lng,
          label: origin.label,
        }))
      } catch {
        return null
      }
    },

    applyTileLayer() {
      if (!this.mapInstance) {
        return
      }

      this.tileLayer?.remove()
      this.tileLayer = L.tileLayer(this.isDark ? darkTiles : lightTiles, {
        attribution: '&copy; OpenStreetMap',
        maxZoom: 19,
      })
      this.tileLayer.addTo(this.mapInstance)
    },

    clearRouteOverlays() {
      this.routeLayer?.clearLayers()
    },

    renderMarkers(path) {
      if (!this.mapInstance || !this.routeLayer) {
        return
      }

      const truckPosition = this.getPointAlongPath(path, this.progress) ?? this.origin

      L.marker([this.origin.lat, this.origin.lng], {
        icon: this.buildMarkerIcon('A', '#3b82f6'),
        title: this.origin.label,
      }).addTo(this.routeLayer)

      L.marker([this.destination.lat, this.destination.lng], {
        icon: this.buildMarkerIcon('B', '#10b981'),
        title: this.destination.label,
      }).addTo(this.routeLayer)

      L.marker([truckPosition.lat, truckPosition.lng], {
        icon: this.buildMarkerIcon('•', '#2563eb'),
        title: this.__('Current shipment position'),
      }).addTo(this.routeLayer)
    },

    async renderRoute() {
      if (!this.mapInstance || !this.routeLayer) {
        return
      }

      this.clearRouteOverlays()
      this.mapError = null

      const drivingRoute = await this.fetchDrivingRoute(this.origin, this.destination)
      const path = drivingRoute ?? this.buildGeodesicRoute(this.origin, this.destination)

      L.polyline(
        path.map(point => [point.lat, point.lng]),
        {
          color: '#3b82f6',
          weight: 4,
          opacity: 0.9,
        },
      ).addTo(this.routeLayer)

      this.renderMarkers(path)
      this.mapInstance.fitBounds(L.latLngBounds(path.map(point => [point.lat, point.lng])), { padding: [48, 48] })
      this.refreshMapSize()
    },

    refreshMapSize() {
      if (!this.mapInstance || !this.$refs.mapContainer) {
        return
      }

      const { width, height } = this.$refs.mapContainer.getBoundingClientRect()

      if (width === 0 || height === 0) {
        return
      }

      this.mapInstance.invalidateSize()
    },

    async initializeMap() {
      if (!this.$refs.mapContainer) {
        return
      }

      this.mapInstance = L.map(this.$refs.mapContainer, {
        zoomControl: false,
        attributionControl: false,
      }).setView([this.origin.lat, this.origin.lng], 5)

      this.routeLayer = L.layerGroup().addTo(this.mapInstance)
      this.applyTileLayer()

      await this.$nextTick()
      this.refreshMapSize()
      window.requestAnimationFrame(() => {
        this.refreshMapSize()
      })

      await this.renderRoute()
    },

    observeMapContainer() {
      if (!this.$refs.mapContainer || typeof ResizeObserver === 'undefined') {
        return
      }

      this.resizeObserver = new ResizeObserver(() => {
        this.refreshMapSize()
      })
      this.resizeObserver.observe(this.$refs.mapContainer)
    },

    zoomIn() {
      this.mapInstance?.zoomIn()
    },

    zoomOut() {
      this.mapInstance?.zoomOut()
    },
  },
}
</script>

<style scoped>
:deep(.leaflet-container) {
  width: 100%;
  height: 100%;
  background: #f4f4f5;
}

:deep(html.dark .leaflet-container),
:deep(.dark .leaflet-container) {
  background: #27272a;
}
</style>
